<template>
    <div class="gs-checkout-rule-editor">
        <section class="gs-checkout-default-card">
            <div class="gs-checkout-default-card__copy">
                <small>Default legal setting</small>
                <h4>Apply this legal rule unless a price point needs its own terms.</h4>
                <p>This keeps the normal checkout policy consistent while still allowing exceptions for specific offers.</p>
            </div>
            <div class="gs-checkout-default-card__body">
                <div class="gs-checkout-choice-row">
                    <button
                        v-for="legalType in legalTypes"
                        :key="legalType.id"
                        type="button"
                        class="gs-checkout-choice"
                        :class="{ 'is-selected': defaultLegalOptions.enabled == legalType.id }"
                        @click="setDefaultLegalType(legalType.id)"
                    >
                        <span></span>
                        <strong>{{ legalType.title }}</strong>
                        <em>{{ legalType.description }}</em>
                    </button>
                </div>
                <div v-if="defaultLegalOptions.enabled == 1" class="gs-checkout-rich-field">
                    <label>{{ $t('frontend_product_step5_legal_text') }}</label>
                    <quill-editor
                        ref="defaultLegalTextEditor"
                        v-model="defaultLegalOptions.html"
                        :options="legalEditorOptions"
                        class="min-h-32"
                        @input="syncDefaultLegalToInherited"
                    ></quill-editor>
                </div>
            </div>
        </section>

        <section class="gs-checkout-price-rules">
            <div class="gs-checkout-price-rules__head">
                <div>
                    <h4>Price point legal settings</h4>
                    <p>Price points can inherit the default legal setting or use a custom override.</p>
                </div>
            </div>

            <article
                v-for="(pricePoint, index) in localModel.pricing_points"
                :key="pricePoint.id || index"
                class="gs-checkout-price-rule"
                :class="{ 'is-custom': !isUsingDefaultLegal(pricePoint) }"
            >
                <header class="gs-checkout-price-rule__head">
                    <div>
                        <strong>{{ pricePoint.name }}</strong>
                        <span>{{ legalSummary(pricePoint) }}</span>
                    </div>
                    <div class="gs-checkout-price-rule__actions">
                        <em :class="{ 'is-custom': !isUsingDefaultLegal(pricePoint) }">
                            {{ isUsingDefaultLegal(pricePoint) ? 'Default' : 'Custom' }}
                        </em>
                        <label>
                            <span>Apply default</span>
                            <gd-toggle
                                aria-label="Apply the default legal settings"
                                :value="isUsingDefaultLegal(pricePoint)"
                                @input="toggleLegalDefault(pricePoint, $event)"
                            />
                        </label>
                        <button type="button" @click="toggleLegalEditor(index)">
                            {{ openLegalIndex === index ? 'Collapse' : 'Edit settings' }}
                        </button>
                    </div>
                </header>

                <div v-if="openLegalIndex === index && !isUsingDefaultLegal(pricePoint)" class="gs-checkout-price-rule__body">
                    <div class="gs-checkout-choice-row">
                        <button
                            v-for="legalType in legalTypes"
                            :key="legalType.id"
                            type="button"
                            class="gs-checkout-choice"
                            :class="{ 'is-selected': pricePoint.legal_options.enabled == legalType.id }"
                            @click="pricePoint.legal_options.enabled = legalType.id"
                        >
                            <span></span>
                            <strong>{{ legalType.title }}</strong>
                            <em>{{ legalType.description }}</em>
                        </button>
                    </div>
                    <div v-if="pricePoint.legal_options.enabled == 1" class="gs-checkout-rich-field">
                        <label>{{ $t('frontend_product_step5_legal_text') }}</label>
                        <quill-editor
                            v-model="pricePoint.legal_options.html"
                            :options="legalEditorOptions"
                            class="min-h-32"
                        ></quill-editor>
                    </div>
                </div>
            </article>
        </section>
    </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor';

export default {
    components: {
        quillEditor,
    },
    props: {
        model: {
            required: true,
        },
        validation: {
            required: true,
        },
    },
    data() {
        const defaultOptions = this.cloneLegalOptions(
            (this.model.checkout_page_options && this.model.checkout_page_options.legal_default_options) ||
            (this.model.pricing_points && this.model.pricing_points[0] && this.model.pricing_points[0].legal_options) ||
            { enabled: 0, html: '' }
        );

        return {
            localModel: this.model,
            defaultLegalOptions: defaultOptions,
            openLegalIndex: null,
            legalEditorOptions: {
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['link'],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                    ],
                },
            },
            legalTypes: [
                {
                    id: 0,
                    title: this.$t('frontend_product_step5_legal_types0_title'),
                    description: this.$t('frontend_product_step5_legal_types0_desc'),
                },
                {
                    id: 1,
                    title: this.$t('frontend_product_step5_legal_types1_title'),
                    description: this.$t('frontend_product_step5_legal_types1_desc'),
                },
            ],
        }
    },
    mounted() {
        this.ensureLegalDefaults();
    },
    watch: {
        localModel: {
            handler() {
                this.$emit('update-model', this.localModel);
                this.$emit('updateModel', this.localModel);
            },
            deep: true,
        },
    },
    methods: {
        cloneLegalOptions(options) {
            return {
                enabled: options && options.enabled != null ? options.enabled : 0,
                html: options && options.html ? options.html : '',
            };
        },
        ensureLegalDefaults() {
            if (!this.localModel.checkout_page_options) {
                this.$set(this.localModel, 'checkout_page_options', {});
            }
            if (!this.localModel.checkout_page_options.legal_default_options) {
                this.$set(this.localModel.checkout_page_options, 'legal_default_options', this.cloneLegalOptions(this.defaultLegalOptions));
            }

            this.localModel.pricing_points.forEach((pricePoint) => {
                if (!pricePoint.legal_options) {
                    this.$set(pricePoint, 'legal_options', this.cloneLegalOptions(this.defaultLegalOptions));
                }
                if (pricePoint.legal_options.use_default == null) {
                    this.$set(pricePoint.legal_options, 'use_default', this.legalMatchesDefault(pricePoint));
                }
                if (pricePoint.legal_options.use_default) {
                    this.applyDefaultLegal(pricePoint);
                }
            });
        },
        legalMatchesDefault(pricePoint) {
            if (!pricePoint.legal_options) return true;
            return String(pricePoint.legal_options.enabled) === String(this.defaultLegalOptions.enabled) &&
                String(pricePoint.legal_options.html || '') === String(this.defaultLegalOptions.html || '');
        },
        isUsingDefaultLegal(pricePoint) {
            return pricePoint.legal_options && pricePoint.legal_options.use_default !== false;
        },
        applyDefaultLegal(pricePoint) {
            const useDefault = pricePoint.legal_options && pricePoint.legal_options.use_default !== false;
            this.$set(pricePoint, 'legal_options', {
                ...this.cloneLegalOptions(this.defaultLegalOptions),
                use_default: useDefault,
            });
        },
        syncDefaultLegalToInherited() {
            this.$set(this.localModel.checkout_page_options, 'legal_default_options', this.cloneLegalOptions(this.defaultLegalOptions));
            this.localModel.pricing_points.forEach((pricePoint) => {
                if (this.isUsingDefaultLegal(pricePoint)) {
                    this.applyDefaultLegal(pricePoint);
                }
            });
        },
        setDefaultLegalType(value) {
            this.defaultLegalOptions.enabled = value;
            this.syncDefaultLegalToInherited();
        },
        toggleLegalDefault(pricePoint, value) {
            const enabled = !!value;
            if (!pricePoint.legal_options) {
                this.$set(pricePoint, 'legal_options', this.cloneLegalOptions(this.defaultLegalOptions));
            }
            this.$set(pricePoint.legal_options, 'use_default', enabled);
            if (enabled) {
                this.applyDefaultLegal(pricePoint);
            }
        },
        toggleLegalEditor(index) {
            const pricePoint = this.localModel.pricing_points[index];
            if (this.isUsingDefaultLegal(pricePoint)) {
                this.toggleLegalDefault(pricePoint, false);
            }
            this.openLegalIndex = this.openLegalIndex === index ? null : index;
        },
        legalSummary(pricePoint) {
            if (this.isUsingDefaultLegal(pricePoint)) return 'Uses the default legal setting';
            if (pricePoint.legal_options && pricePoint.legal_options.enabled == 1) return 'Custom terms are required at checkout';
            return 'Custom setting: no extra legal confirmation';
        },
    },
    beforeDestroy() {
        if (typeof this.legalTextEditor !== 'undefined') {
            this.legalTextEditor.destroy();
        }
    }
}
</script>
